import { Breadcrumb, PageHeader } from '@orbitalwatch/design';
import type { IBreadcrumbItem } from '@orbitalwatch/design';
import { useMemo } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePageAside } from './pageAside';
import { useTabTitle } from './tabTitle';

export interface BreadcrumbItem {
    text: string;
    /** In-app route: rendered as a link, followed with the router. */
    to?: string;
    onClick?: () => void;
}

export interface PageProps {
    /** The h1. Omit it (with `header={false}`) on screens that draw their own header (the reader). */
    title?: ReactNode;
    /**
     * `document.title` = `<tabTitle> | Ark`. Ark's convention is "<Page> | <Space> | Ark": pass
     * `tabTitle={[pageTitle, spaceName]}` (empty parts are dropped). Defaults to a string `title`.
     */
    tabTitle?: string | (string | null | undefined)[];
    breadcrumb?: BreadcrumbItem[];
    /** Uppercase section label ("Space settings"). */
    eyebrow?: ReactNode;
    /** Chips in the eyebrow row (a state chip, a lock badge). */
    status?: ReactNode;
    /** 12 px text-3 note in the eyebrow row ("Updated 2 h ago"). */
    meta?: ReactNode;
    /** 15 px text-2 intro under the title. */
    intro?: ReactNode;
    /** Buttons on the right. */
    actions?: ReactNode;
    /** `false`: no breadcrumb / PageHeader (the screen renders its own head). Default `true`. */
    header?: boolean;
    /** Pages fill `<main>`. `reading`: the 1000 px reading column of an embedded document. Default `default`. */
    width?: 'default' | 'reading';
    className?: string;
    children?: ReactNode;
}

/**
 * Root of every routed screen: library `Breadcrumb` over the library `PageHeader`, then the content (and, on
 * phones, what the shell puts under the header). Sets `document.title`.
 */
export function Page(props: PageProps) {
    const navigate = useNavigate();
    const aside = usePageAside();
    const { breadcrumb } = props;
    useTabTitle(props.tabTitle ?? (typeof props.title === 'string' ? props.title : undefined));

    const items = useMemo((): IBreadcrumbItem[] => (breadcrumb ?? []).map((item, index) => {
        const to = item.to;
        return {
            key: `${index}-${item.text}`,
            text: item.text,
            href: to,
            onClick: to ? () => navigate(to) : item.onClick,
        };
    }), [breadcrumb, navigate]);

    const width = props.width && props.width !== 'default' ? `is-${props.width}` : '';
    const showHeader = props.header !== false && props.title !== undefined;

    return (
        <div className={['ark-page', width, props.className].filter(Boolean).join(' ')}>
            {showHeader ? (
                <div className="ark-page-head">
                    {items.length > 0 ? <Breadcrumb items={items} /> : null}
                    <PageHeader
                        eyebrow={props.eyebrow}
                        status={props.status}
                        meta={props.meta}
                        title={props.title}
                        intro={props.intro}
                        actions={props.actions}
                    />
                </div>
            ) : null}
            {aside}
            {props.children}
        </div>
    );
}
